import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Box,
  Typography,
  Skeleton,
  Chip,
  Tooltip,
  TableSortLabel,
} from "@mui/material";

import {
  SchoolData,
  schoolVaccinationData,
  loadVaccinationData,
} from "../data/dataTable";
import { useNavigate } from "react-router-dom";
import DynamicHelmet from "./DynamicHelmet";
import SearchAndFilterBar from "./SearchAndFilterBar";
import {
  SCHOOL_TYPE_COLORS,
  RISK_COLORS,
  RISK_RANGES,
  getVaccinationRiskColor,
} from "./vaccinationUtils";

// Skeleton loader for table rows
const TableRowSkeleton = () => (
  <TableRow>
    <TableCell>
      <Skeleton variant="text" width="100%" />
    </TableCell>
    <TableCell>
      <Skeleton variant="text" width="100%" />
    </TableCell>
    <TableCell align="center">
      <Skeleton variant="text" width="60%" sx={{ mx: "auto" }} />
    </TableCell>
    <TableCell align="center">
      <Skeleton
        variant="rounded"
        width="70px"
        height="32px"
        sx={{ mx: "auto" }}
      />
    </TableCell>
    <TableCell align="center">
      <Skeleton
        variant="rounded"
        width="70px"
        height="32px"
        sx={{ mx: "auto" }}
      />
    </TableCell>
    <TableCell align="center">
      <Skeleton variant="text" width="50%" sx={{ mx: "auto" }} />
    </TableCell>
    <TableCell align="center">
      <Skeleton variant="circular" width={30} height={30} sx={{ mx: "auto" }} />
    </TableCell>
  </TableRow>
);

// Type for sort order
type Order = "asc" | "desc";

// Type for sortable fields
type SortableField =
  | "district"
  | "schoolName"
  | "kindergartners"
  | "allVaccinations"
  | "mmr"
  | "medicalExemption";

const SchoolVaccinationTable: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [schools, setSchools] = useState<SchoolData[]>(schoolVaccinationData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [schoolTypeFilter, setSchoolTypeFilter] = useState<string>("All");
  const [riskLevelFilter, setRiskLevelFilter] = useState<string>("All");
  const navigate = useNavigate();

  // Sorting state
  const [orderBy, setOrderBy] = useState<SortableField>("schoolName");
  const [order, setOrder] = useState<Order>("asc");

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Load the real data when component mounts
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const data = await loadVaccinationData();
        if (data.length > 0) {
          setSchools(data);
        }
        setLoading(false);
      } catch (err) {
        console.error("Error loading vaccination data:", err);
        setError("Failed to load vaccination data. Please try again later.");
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Function to handle sort request
  const handleRequestSort = (property: SortableField) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setPage(0); // Reset to first page when sort changes
  };

  // Function to sort schools
  const sortSchools = (
    schools: SchoolData[],
    orderBy: SortableField,
    order: Order
  ): SchoolData[] => {
    return [...schools].sort((a, b) => {
      // For the district column, we need to handle the 'none' case
      if (orderBy === "district") {
        const aValue =
          a.district.toLowerCase() === "none" ? a.county : a.district;
        const bValue =
          b.district.toLowerCase() === "none" ? b.county : b.district;

        return order === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      // Handle numeric fields
      if (orderBy === "kindergartners") {
        return order === "asc"
          ? a.kindergartners - b.kindergartners
          : b.kindergartners - a.kindergartners;
      }

      if (orderBy === "allVaccinations") {
        return order === "asc"
          ? a.allVaccinations - b.allVaccinations
          : b.allVaccinations - a.allVaccinations;
      }

      if (orderBy === "mmr") {
        return order === "asc" ? a.mmr - b.mmr : b.mmr - a.mmr;
      }

      if (orderBy === "medicalExemption") {
        return order === "asc"
          ? a.medicalExemption - b.medicalExemption
          : b.medicalExemption - a.medicalExemption;
      }

      // For schoolName field
      if (orderBy === "schoolName") {
        return order === "asc"
          ? a.schoolName.localeCompare(b.schoolName)
          : b.schoolName.localeCompare(a.schoolName);
      }

      // Fallback that should never happen
      return 0;
    });
  };

  // Memoize the filtered schools to avoid recalculating on every render
  const filteredSchools = useMemo(() => {
    let filtered = schools;

    // Apply school type filter
    if (schoolTypeFilter !== "All") {
      filtered = filtered.filter(
        (school) => school.schoolType === schoolTypeFilter
      );
    }

    // Apply risk level filter
    if (riskLevelFilter !== "All") {
      const range = RISK_RANGES[riskLevelFilter as keyof typeof RISK_RANGES];
      filtered = filtered.filter(
        (school) =>
          school.allVaccinations >= range.min &&
          school.allVaccinations <= range.max
      );
    }

    // Apply search term filter
    if (searchTerm.trim()) {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (school) =>
          school.schoolName.toLowerCase().includes(lowerCaseSearchTerm) ||
          school.district.toLowerCase().includes(lowerCaseSearchTerm)
      );
    }

    // Apply sorting
    return sortSchools(filtered, orderBy, order);
  }, [schools, searchTerm, schoolTypeFilter, riskLevelFilter, orderBy, order]);

  // Memoize the current page of schools to avoid recalculating on every render
  const currentPageSchools = useMemo(() => {
    return filteredSchools.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [filteredSchools, page, rowsPerPage]);

  // Reset to first page when search or filters change
  useEffect(() => {
    setPage(0);
  }, [searchTerm, schoolTypeFilter, riskLevelFilter]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDetailClick = (schoolId: number) => {
    const school = schools.find((s) => s.id === schoolId);
    if (school) {
      const params = new URLSearchParams({
        schoolCode: school.schoolCode || "",
        schoolName: encodeURIComponent(school.schoolName),
      });
      navigate(`/school?${params.toString()}`);
    }
  };

  // Clear all filters function
  const clearAllFilters = () => {
    setSearchTerm("");
    setSchoolTypeFilter("All");
    setRiskLevelFilter("All");
  };

  // Show table skeleton during loading
  const renderTableBody = () => {
    if (loading) {
      return Array.from(new Array(10)).map((_, index) => (
        <TableRowSkeleton key={index} />
      ));
    }

    if (currentPageSchools.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={7} align="center">
            <Typography variant="body1" sx={{ py: 2 }}>
              No schools match your search criteria.
            </Typography>
          </TableCell>
        </TableRow>
      );
    }

    return currentPageSchools.map((school) => {
      const vaccinationRisk = getVaccinationRiskColor(school.allVaccinations);

      return (
        <TableRow
          key={school.id}
          hover
          sx={{
            "&:hover": {
              backgroundColor: (theme) => theme.palette.action.hover,
            },
          }}
        >
          <TableCell>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography variant="body1">
                {school.district.toLowerCase() === "none"
                  ? school.county
                  : school.district}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  fontSize: "0.875rem",
                  fontStyle: "italic",
                }}
              >
                {school.district.toLowerCase() === "none"
                  ? ""
                  : `${school.county} County`}
              </Typography>
            </Box>
          </TableCell>
          <TableCell>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "4px",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontWeight: "600",
                  color: "var(--blue-20)",
                  textDecoration: "underline",
                  textDecorationColor: "var(--blue-40)",
                  textUnderlineOffset: "2px",
                  cursor: "pointer",
                  "&:hover": {
                    opacity: 0.8,
                  },
                }}
                onClick={() => handleDetailClick(school.id)}
              >
                {school.schoolName}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  backgroundColor:
                    SCHOOL_TYPE_COLORS[
                      school.schoolType as keyof typeof SCHOOL_TYPE_COLORS
                    ],
                  color: "white",
                  fontSize: "0.75rem",
                  padding: "2px 8px",
                  borderRadius: "12px",
                  display: "inline-block",
                  width: "fit-content",
                  fontWeight: "bold",
                }}
              >
                {school.schoolType}
              </Typography>
            </Box>
          </TableCell>
          <TableCell align="center">{school.kindergartners}</TableCell>
          <TableCell align="center">
            <Tooltip title={vaccinationRisk.label} arrow placement="top">
              <Chip
                label={
                  school.allVaccinations % 1 === 0
                    ? Math.round(school.allVaccinations) + "%"
                    : school.allVaccinations.toFixed(1) + "%"
                }
                sx={{
                  backgroundColor: vaccinationRisk.color,
                  color:
                    vaccinationRisk.color === RISK_COLORS.moderate
                      ? "rgba(0, 0, 0, 0.87)"
                      : "white",
                  fontWeight: "bold",
                  minWidth: "70px",
                }}
              />
            </Tooltip>
          </TableCell>
          <TableCell align="center">
            <Tooltip
              title={getVaccinationRiskColor(school.mmr).label}
              arrow
              placement="top"
            >
              <Chip
                label={
                  school.mmr % 1 === 0
                    ? Math.round(school.mmr) + "%"
                    : school.mmr.toFixed(1) + "%"
                }
                sx={{
                  backgroundColor: getVaccinationRiskColor(school.mmr).color,
                  color:
                    getVaccinationRiskColor(school.mmr).color ===
                    RISK_COLORS.moderate
                      ? "rgba(0, 0, 0, 0.87)"
                      : "white",
                  fontWeight: "bold",
                  minWidth: "70px",
                }}
              />
            </Tooltip>
          </TableCell>
          <TableCell align="center">
            {school.medicalExemption % 1 === 0
              ? Math.round(school.medicalExemption) + "%"
              : school.medicalExemption.toFixed(1) + "%"}
          </TableCell>
        </TableRow>
      );
    });
  };

  return (
    <Box sx={{ width: "100%", mb: 4 }}>
      <DynamicHelmet
        title="California School Vaccinations Database | EdSource"
        description="Use this resource to check vaccination rates at traditional public, public charter, and private schools across California. Search and filter by school type and vaccination rate, or explore the data with a map."
        url="/"
      />

      <Box sx={{ width: "100%", p: 2 }}>
        <Typography
          className="intro-text"
          variant="body1"
          color="text.secondary"
          sx={{ mb: 2 }}
        >
          School vaccination rates have come into greater focus as Texas
          grapples with a deadly measles outbreak and federal health and
          education revisit long-standing vaccination requirements for entry
          into public schools. At the same time, the most recent data indicates
          a drop in kindergarten vaccination rates that's brought some areas of
          California below herd immunity, increasing the possibility of a
          Texas-like outbreak in some parts of the state.{" "}
          <a href="https://edsource.org/2025/amid-deadly-measles-outbreak-californias-childhood-vaccination-rates-are-falling/730180">
            Read EdSource's coverage here
          </a>
          . Use this resource to check vaccination rates at your own school.
          Filter by school type and vaccination rate, and use the search bar to
          find results by individual school name or district.
        </Typography>

        {/* Search and Filter Bar */}
        <SearchAndFilterBar
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          schoolTypeFilter={schoolTypeFilter}
          setSchoolTypeFilter={setSchoolTypeFilter}
          riskLevelFilter={riskLevelFilter}
          setRiskLevelFilter={setRiskLevelFilter}
          filteredSchools={filteredSchools}
          clearAllFilters={clearAllFilters}
        />

        {/* Error message */}
        {error && (
          <Box sx={{ color: "error.main", textAlign: "center", my: 2 }}>
            <Typography>{error}</Typography>
          </Box>
        )}

        {/* Table */}
        <Paper sx={{ width: "100%", mb: 2, overflow: "hidden" }}>
          {/* Results summary */}
          {!loading && filteredSchools.length > 0 && (
            <Box
              sx={{ p: 2, borderBottom: "1px solid rgba(224, 224, 224, 1)" }}
            >
              <Typography variant="body2" color="text.secondary">
                Showing {page * rowsPerPage + 1}-
                {Math.min((page + 1) * rowsPerPage, filteredSchools.length)} of{" "}
                {filteredSchools.length}{" "}
                {filteredSchools.length === 1 ? "school" : "schools"}
                {schoolTypeFilter !== "All" || riskLevelFilter !== "All"}
              </Typography>
            </Box>
          )}

          {/* Table Content */}
          <TableContainer
            sx={{ maxHeight: "calc(100vh - 350px)", overflowY: "auto" }}
          >
            <Table stickyHeader>
              <TableHead className="sticky-table-header">
                <TableRow>
                  <TableCell
                    sortDirection={orderBy === "district" ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === "district"}
                      direction={orderBy === "district" ? order : "asc"}
                      onClick={() => handleRequestSort("district")}
                    >
                      District/County
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sortDirection={orderBy === "schoolName" ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === "schoolName"}
                      direction={orderBy === "schoolName" ? order : "asc"}
                      onClick={() => handleRequestSort("schoolName")}
                    >
                      School
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    align="center"
                    sortDirection={orderBy === "kindergartners" ? order : false}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === "kindergartners"}
                        direction={orderBy === "kindergartners" ? order : "asc"}
                        onClick={() => handleRequestSort("kindergartners")}
                      >
                        Kindergartners
                      </TableSortLabel>
                      <Typography
                        variant="caption"
                        sx={{ fontSize: "0.7rem", color: "text.secondary" }}
                      >
                        2023-24
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell
                    align="center"
                    sortDirection={
                      orderBy === "allVaccinations" ? order : false
                    }
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === "allVaccinations"}
                        direction={
                          orderBy === "allVaccinations" ? order : "asc"
                        }
                        onClick={() => handleRequestSort("allVaccinations")}
                      >
                        All Vaccinations (%)
                      </TableSortLabel>
                      <Typography
                        variant="caption"
                        sx={{ fontSize: "0.7rem", color: "text.secondary" }}
                      >
                        2023-24
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell
                    align="center"
                    sortDirection={orderBy === "mmr" ? order : false}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === "mmr"}
                        direction={orderBy === "mmr" ? order : "asc"}
                        onClick={() => handleRequestSort("mmr")}
                      >
                        MMR* (%)
                      </TableSortLabel>
                      <Typography
                        variant="caption"
                        sx={{ fontSize: "0.7rem", color: "text.secondary" }}
                      >
                        2023-24
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell
                    align="center"
                    sortDirection={
                      orderBy === "medicalExemption" ? order : false
                    }
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === "medicalExemption"}
                        direction={
                          orderBy === "medicalExemption" ? order : "asc"
                        }
                        onClick={() => handleRequestSort("medicalExemption")}
                      >
                        Medical Exemption** (%)
                      </TableSortLabel>
                      <Typography
                        variant="caption"
                        sx={{ fontSize: "0.7rem", color: "text.secondary" }}
                      >
                        2023-24
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{renderTableBody()}</TableBody>
            </Table>
          </TableContainer>

          {!loading && (
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={filteredSchools.length}
              rowsPerPage={rowsPerPage}
              page={
                filteredSchools.length <= page * rowsPerPage && page > 0
                  ? 0
                  : page
              }
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Paper>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 2,
            color: "text.secondary",
            fontSize: ".9rem",
            fontStyle: "italic",
          }}
        >
          *Measles, Mumps, and Rubella (MMR); <br />
          ** A Permanent Medical Exemption (PME): granted upon the filing with
          the school of a written statement from a licensed physician to the
          effect that the physical condition of the student or medical
          circumstances relating to the student are such that immunization is
          permanently not indicated.
        </Typography>
      </Box>
    </Box>
  );
};

export default SchoolVaccinationTable;
